import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Form, Button, Modal } from 'react-bootstrap';
import SettingsService from '../services/SettingsService';
import { leaveTypeService } from '../services/leaveTypeService';
import Spinner from './Spinner';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResponsiveTable from './ResponsiveTable';

const LeaveSettings = () => {
    // Settings State
    const [settings, setSettings] = useState({
        DefaultAnnualLeaveDays: 0,
        DefaultSickLeaveDays: 0,
        MaxConsecutiveLeaveDays: 1,
        AllowLeaveCarryOver: false,
        MaxCarryOverDays: 0
    });

    // Leave Types State
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedLeaveType, setSelectedLeaveType] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        requiresDocumentation: false,
        isActive: true,
        defaultDays: 0,
        isHourly: false,
        deductFromAllowance: true
    });

    const [isLoading, setIsLoading] = useState({
        settings: true,
        leaveTypes: true
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState({});

    // Fetch Initial Data
    useEffect(() => {
        fetchSettings();
        fetchLeaveTypes();
    }, []);

    // Settings Functions
    const fetchSettings = async () => {
        setIsLoading(prev => ({ ...prev, settings: true }));
        try {
            const response = await SettingsService.getSetting('LeaveAllowance');
            if (response && response.value) {
                try {
                    const parsedSettings = JSON.parse(response.value);
                    // Extract the actual settings from potentially nested structure
                    const cleanSettings = {
                        DefaultAnnualLeaveDays: parsedSettings.DefaultAnnualLeaveDays || 0,
                        DefaultSickLeaveDays: parsedSettings.DefaultSickLeaveDays || 0,
                        MaxConsecutiveLeaveDays: parsedSettings.MaxConsecutiveLeaveDays || 1,
                        AllowLeaveCarryOver: parsedSettings.AllowLeaveCarryOver || false,
                        MaxCarryOverDays: parsedSettings.MaxCarryOverDays || 0
                    };
                    setSettings(cleanSettings);
                } catch (parseError) {
                    console.error('Error parsing settings:', parseError);
                    setSettings({
                        DefaultAnnualLeaveDays: 0,
                        DefaultSickLeaveDays: 0,
                        MaxConsecutiveLeaveDays: 1,
                        AllowLeaveCarryOver: false,
                        MaxCarryOverDays: 0
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load leave settings');
        } finally {
            setIsLoading(prev => ({ ...prev, settings: false }));
        }
    };

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : Number(value)
        }));
    };

    const saveSettings = async () => {
        setIsSaving(true);
        try {
            // Send only the clean settings object without nesting
            await SettingsService.updateSetting('LeaveAllowance', settings);
            toast.success('Settings saved successfully!');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    // Leave Types Functions
    const fetchLeaveTypes = async () => {
        setIsLoading(prev => ({ ...prev, leaveTypes: true }));
        try {
            const data = await leaveTypeService.getAllLeaveTypes();
            setLeaveTypes(data);
        } catch (error) {
            console.error('Error fetching leave types:', error);
            toast.error('Failed to load leave types');
        } finally {
            setIsLoading(prev => ({ ...prev, leaveTypes: false }));
        }
    };

    const handleModalShow = (leaveType = null) => {
        if (leaveType) {
            setFormData({
                name: leaveType.name,
                description: leaveType.description,
                requiresDocumentation: leaveType.requiresDocumentation,
                isActive: leaveType.isActive,
                defaultDays: leaveType.defaultDays,
                isHourly: leaveType.isHourly,
                deductFromAllowance: leaveType.deductFromAllowance
            });
            setSelectedLeaveType(leaveType);
        } else {
            setFormData({
                name: '',
                description: '',
                requiresDocumentation: false,
                isActive: true,
                defaultDays: 0,
                isHourly: false,
                deductFromAllowance: true
            });
            setSelectedLeaveType(null);
        }
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedLeaveType(null);
        setFormData({ name: '', description: '', isActive: true });
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            
            // Reset defaultDays to 0 when switching to hourly
            if (name === 'isHourly' && value === true) {
                newData.defaultDays = 0;
            }
            
            return newData;
        });
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            if (selectedLeaveType) {
                await leaveTypeService.updateLeaveType(selectedLeaveType.id, formData);
                toast.success('Leave type updated successfully!');
            } else {
                await leaveTypeService.createLeaveType(formData);
                toast.success('Leave type created successfully!');
            }
            fetchLeaveTypes();
            handleModalClose();
        } catch (error) {
            console.error('Error saving leave type:', error);
            toast.error(selectedLeaveType ? 'Failed to update leave type' : 'Failed to create leave type');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this leave type?')) {
            setIsDeleting(prev => ({ ...prev, [id]: true }));
            try {
                await leaveTypeService.deleteLeaveType(id);
                fetchLeaveTypes();
                toast.success('Leave type deleted successfully!');
            } catch (error) {
                console.error('Error deleting leave type:', error);
                toast.error('Failed to delete leave type');
            } finally {
                setIsDeleting(prev => ({ ...prev, [id]: false }));
            }
        }
    };

    const leaveTypeColumns = [
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { 
            key: 'type', 
            label: 'Type',
            render: (_, item) => item.isHourly ? 'Hourly' : 'Daily'
        },
        { 
            key: 'defaultDays', 
            label: 'Default Days',
            render: (_, item) => item.isHourly ? '-' : `${item.defaultDays} days`
        },
        { 
            key: 'requiresDocumentation', 
            label: 'Documentation Required',
            render: (value) => value ? 'Yes' : 'No'
        },
        { 
            key: 'deductFromAllowance', 
            label: 'Deduct From Allowance',
            render: (value) => value ? 'Yes' : 'No'
        },
        { 
            key: 'status', 
            label: 'Status',
            render: (_, item) => item.isActive ? 'Active' : 'Inactive'
        }
    ];

    const leaveTypeActions = [
        {
            label: 'Edit',
            variant: 'info',
            onClick: (item) => handleModalShow(item)
        },
        {
            label: 'Delete',
            variant: 'danger',
            onClick: (item) => handleDelete(item.id)
        }
    ];

    return (
        <div className="settings-container container">
            <Tabs defaultActiveKey="settings" className="mb-3">
                {/* Settings Tab */}
                <Tab eventKey="settings" title="Leave Settings">
                    {isLoading.settings ? (
                        <Spinner />
                    ) : (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Default Annual Leave Days</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="DefaultAnnualLeaveDays"
                                    value={settings.DefaultAnnualLeaveDays}
                                    onChange={handleSettingsChange}
                                    min="0"
                                    max="365"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Default Sick Leave Days</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="DefaultSickLeaveDays"
                                    value={settings.DefaultSickLeaveDays}
                                    onChange={handleSettingsChange}
                                    min="0"
                                    max="365"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Maximum Consecutive Leave Days</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="MaxConsecutiveLeaveDays"
                                    value={settings.MaxConsecutiveLeaveDays}
                                    onChange={handleSettingsChange}
                                    min="1"
                                    max="365"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Allow Leave Carry Over"
                                    name="AllowLeaveCarryOver"
                                    checked={settings.AllowLeaveCarryOver}
                                    onChange={handleSettingsChange}
                                />
                            </Form.Group>

                            {settings.AllowLeaveCarryOver && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Maximum Carry Over Days</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="MaxCarryOverDays"
                                        value={settings.MaxCarryOverDays}
                                        onChange={handleSettingsChange}
                                        min="0"
                                        max="365"
                                    />
                                </Form.Group>
                            )}

                            <Button 
                                variant="primary" 
                                onClick={saveSettings}
                                disabled={isSaving}
                                className="mt-3"
                            >
                                {isSaving ? <Spinner /> : 'Save Settings'}
                            </Button>
                        </Form>
                    )}
                </Tab>

                {/* Leave Types Tab */}
                <Tab eventKey="leaveTypes" title="Leave Types">
                    <div className="mb-3">
                        <Button variant="primary" onClick={() => handleModalShow()}>
                            Add New Leave Type
                        </Button>
                    </div>

                    {isLoading.leaveTypes ? (
                        <Spinner />
                    ) : (
                        <ResponsiveTable 
                            data={leaveTypes}
                            columns={leaveTypeColumns}
                            actions={leaveTypeActions}
                        />
                    )}

                    {/* Leave Type Modal */}
                    <Modal show={showModal} onHide={handleModalClose}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {selectedLeaveType ? 'Edit Leave Type' : 'Add New Leave Type'}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                        required
                                        maxLength={50}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows={3}
                                        maxLength={500}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Leave Type</Form.Label>

                                    <Form.Check
                                        type="radio"
                                        label="Hourly"
                                        name="isHourly"
                                        checked={formData.isHourly}
                                        onChange={() => handleFormChange({ target: { name: 'isHourly', value: true }})}
                                        id="hourly-radio"
                                    />
                                    <Form.Check
                                        type="radio"
                                        label="Daily"
                                        name="isHourly"
                                        checked={!formData.isHourly}
                                        onChange={() => handleFormChange({ target: { name: 'isHourly', value: false }})}
                                        id="daily-radio"
                                    />
                                
                                </Form.Group>

                                {!formData.isHourly && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Default Days</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="defaultDays"
                                            value={formData.defaultDays}
                                            onChange={handleFormChange}
                                            min={0}
                                            max={365}
                                        />
                                        <Form.Text className="text-muted">
                                            Maximum 365 days per year
                                        </Form.Text>
                                    </Form.Group>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Requires Documentation"
                                        name="requiresDocumentation"
                                        checked={formData.requiresDocumentation}
                                        onChange={handleFormChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Deduct From Leave Allowance"
                                        name="deductFromAllowance"
                                        checked={formData.deductFromAllowance}
                                        onChange={handleFormChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Active"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleFormChange}
                                    />
                                </Form.Group>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleModalClose}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner /> : (selectedLeaveType ? 'Update' : 'Create')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Tab>
            </Tabs>
        </div>
    );
};

export default LeaveSettings; 